<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <!--tabbar-->
      <slot />
      <!--search panel-->
      <div class="flex items-center gap-2">
        <div class="relative">
          <el-input
            placeholder="Search..."
            prefix-icon="el-icon-search"
            class="w-[250px]"
            clearable
            v-model="searchText"
          />
        </div>
        <el-popover
          placement="bottom"
          trigger="click"
          width="320"
          v-model="filterPanelVisible"
        >
          <button
            slot="reference"
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 w-10"
            type="button"
          >
            <i class="icon-funnel h-4 w-4" />
          </button>
          <ETagFilterPanel @apply="handleApply" />
        </el-popover>
      </div>
    </div>
    <div class="flex-1 relative overflow-hidden">
      <el-scrollbar class="absolute inset-0">
        <div v-if="isLoading">
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6"
          >
            <div
              v-for="n in 6"
              :key="`skeleton-${n}`"
              class="rounded-lg border bg-card text-card-foreground shadow-2xs animate-pulse"
              style="min-height: 340px"
            >
              <div class="flex flex-col space-y-1.5 p-4">
                <div class="flex justify-between items-start">
                  <div>
                    <div class="h-6 w-32 bg-gray-200 rounded mb-2"></div>
                    <div class="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                  <div class="h-10 w-10 bg-gray-200 rounded-full"></div>
                </div>
              </div>
              <div class="flex flex-col p-4 pt-0 gap-2">
                <AspectRatio aspect-ratio="16:9">
                  <div class="w-full h-full bg-gray-200 mb-4 rounded"></div>
                </AspectRatio>
                <div class="flex flex-col text-sm">
                  <div class="flex justify-between gap-1">
                    <span class="text-muted-foreground">Supported Sizes:</span>
                    <div class="h-5 w-10 bg-gray-200 rounded-full"></div>
                  </div>
                </div>
              </div>
              <div class="flex items-center p-4 pt-0">
                <div class="w-full flex justify-between gap-2">
                  <div class="h-9 w-28 bg-gray-200 rounded"></div>
                  <div class="h-9 w-36 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <div
            v-for="template in templates"
            :key="getTemplateKey(template)"
            class="rounded-lg border bg-card text-card-foreground shadow-2xs"
            data-v0-t="card"
          >
            <div class="flex flex-col space-y-1.5 p-4">
              <div class="flex justify-between items-start">
                <div>
                  <h3
                    class="text-2xl font-semibold leading-none tracking-tight"
                  >
                    {{ getTemplateName(template) }}
                  </h3>
                  <p class="text-sm text-muted-foreground">
                    {{ getTemplateDescription(template) }}
                  </p>
                </div>
                <el-popover
                  placement="bottom-end"
                  trigger="click"
                  :visible.sync="popoverVisible[getTemplateKey(template)]"
                  popper-class="min-w-[8rem] p-1"
                >
                  <div
                    data-side="bottom"
                    data-align="end"
                    role="menu"
                    aria-orientation="vertical"
                    data-state="open"
                    data-radix-menu-content=""
                    dir="ltr"
                    data-slot="dropdown-menu-content"
                    class="min-w-[8rem]"
                    data-orientation="vertical"
                    style="outline: currentcolor; pointer-events: auto"
                  >
                    <div
                      role="menuitem"
                      data-slot="dropdown-menu-item"
                      data-variant="default"
                      class="focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none"
                      tabindex="-1"
                      data-orientation="vertical"
                      @click="onPreviewTemplate(template)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-external-link h-4 w-4 mr-2"
                      >
                        <path d="M15 3h6v6"></path>
                        <path d="M10 14 21 3"></path>
                        <path
                          d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                        ></path>
                      </svg>
                      Preview
                    </div>
                    <template v-if="false">
                      <div
                        role="separator"
                        aria-orientation="horizontal"
                        data-slot="dropdown-menu-separator"
                        class="bg-gray-500 -mx-1 my-1 h-px"
                      ></div>
                      <div
                        class="focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none text-red-600"
                        @click="onDeleteTemplate(template)"
                      >
                        <i class="fa fa-trash-o h-4 w-4 mr-2" />
                        Delete
                      </div>
                    </template>
                  </div>
                  <button
                    slot="reference"
                    class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-10 w-10"
                    type="button"
                    @click.stop="togglePopover(getTemplateKey(template))"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-ellipsis-vertical h-4 w-4"
                    >
                      <circle cx="12" cy="12" r="1"></circle>
                      <circle cx="12" cy="5" r="1"></circle>
                      <circle cx="12" cy="19" r="1"></circle>
                    </svg>
                  </button>
                </el-popover>
              </div>
            </div>
            <div class="p-4 pt-0">
              <div class="bg-muted mb-4 overflow-hidden">
                <AspectRatio aspect-ratio="16:9">
                  <SmartImage
                    :src="getTemplateImageUrl(template)"
                    class="w-full h-full"
                  />
                </AspectRatio>
              </div>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Supported Sizes:</span>
                  <div class="flex flex-wrap gap-1 justify-end">
                    <template v-if="getSupportedSizes(template).length">
                      <div
                        v-for="size in getSupportedSizes(template)"
                        :key="`${getTemplateKey(template)}-${size}`"
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground text-xs"
                        data-v0-t="badge"
                      >
                        {{ size }}
                      </div>
                    </template>
                    <span v-else class="text-xs text-muted-foreground">
                      N/A
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex items-center p-4 pt-0">
              <div class="w-full flex justify-between">
                <!-- <el-button icon="fa fa-edit"> Customize </el-button> -->
                <span></span>
                <el-button
                  @click="handleApplyToDisplays(template)"
                  type="primary"
                  icon="el-icon-monitor"
                >
                  Apply to Displays
                </el-button>
              </div>
            </div>
          </div>
          <!--add button-->
          <!-- <div
            class="rounded-lg bg-card text-card-foreground shadow-2xs border-dashed border-2 cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors"
            @click="onOpenAddWebSite"
          >
            <div
              class="p-6 flex flex-col items-center justify-center h-full min-h-[300px]"
            >
              <div
                class="rounded-full bg-muted w-12 h-12 flex items-center justify-center mb-4"
              >
                <i class="fa fa-plus text-lg" />
              </div>
              <h3 class="text-lg font-medium mb-2">Create New Template</h3>
              <p class="text-sm text-muted-foreground text-center mb-4">
                Design a custom template for your digital displays
              </p>
              <el-button type="primary" icon="fa fa-plus">
                Create Template
              </el-button>
            </div>
          </div> -->
        </div>
      </el-scrollbar>
    </div>
    <div class="flex mt-4 justify-end">
      <el-pagination
        @size-change="(val) => tableCtrl.setPageSize(val)"
        @current-change="(val) => tableCtrl.setCurrentPage(val)"
        :current-page="tableCtrl.state.pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="tableCtrl.state.pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="tableCtrl.state.pagination.total"
      />
    </div>
  </div>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import { getEslTemplates, downloadFile } from "@/api";
import AspectRatio from "@/components/AspectRatio.vue";
import ETagFilterPanel from "./ETagFilterPanel.vue";
import SmartImage from "@/components/SmartImage.vue";

export default {
  name: "TemplatesLibrary",
  components: {
    AspectRatio,
    ETagFilterPanel,
    SmartImage,
  },
  props: {
    searchText: {
      type: String,
      default: "",
    },
    queryParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tableCtrl: null,
      popoverVisible: {},
      filterPanelVisible: false,
    };
  },
  computed: {
    templates() {
      return this.tableCtrl?.state.tableData ?? [];
    },
    isLoading() {
      return this.tableCtrl?.state.loading ?? false;
    },
  },
  watch: {
    searchText() {
      if (this.tableCtrl?.isClientPaged) {
        this.tableCtrl.filterData();
      }
    },
    queryParams: {
      deep: true,
      handler() {
        this.reloadTemplates();
      },
    },
  },
  created() {
    this.initTableController();
  },
  methods: {
    togglePopover(key) {
      this.$set(this.popoverVisible, key, !this.popoverVisible[key]);
    },
    closePopover(key) {
      this.$set(this.popoverVisible, key, false);
    },
    onPreviewTemplate(template) {
      this.closePopover(this.getTemplateKey(template));
      // Implement preview logic here
      this.$emit("preview-template", template);
    },
    onDeleteTemplate(template) {
      this.closePopover(this.getTemplateKey(template));
      // Implement delete logic here
      this.$emit("delete-template", template);
    },
    initTableController() {
      this.tableCtrl = new TableController({
        loadMethod: this.fetchTemplates,
        pageSize: 12,
        isClientPaged: true,
        keyField: "id",
        customFilter: (list) => this.applySearchFilter(list),
      });
    },
    async fetchTemplates() {
      try {
        const response = await getEslTemplates(this.queryParams);
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.list)
          ? response.list
          : [];
        const normalized = list.map((item, index) => ({
          ...item,
          id:
            item.id ||
            item.esltemplate_id ||
            `${item.esltemplate_name || "template"}-${index}`,
        }));
        return { list: normalized, totalCount: normalized.length };
      } catch (error) {
        console.error("Failed to load ESL templates", error);
        return { list: [], totalCount: 0 };
      }
    },
    reloadTemplates() {
      if (this.tableCtrl) {
        this.tableCtrl.loadTableData();
      }
    },
    applySearchFilter(list = []) {
      const search = (this.searchText || "").trim().toLowerCase();
      if (!search) {
        return list;
      }
      return list.filter((item = {}) => {
        const tokens = [
          item.esltemplate_name,
          item.esltype_code,
          item.esltemplate_default ? "default" : "",
        ];
        return tokens.some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(search)
        );
      });
    },
    getTemplateKey(template = {}) {
      return (
        template.id ||
        template.esltemplate_id ||
        template.esltemplate_name ||
        template.esltype_code ||
        "template"
      );
    },
    getTemplateName(template = {}) {
      return template.esltemplate_name || template.name || "Template";
    },
    getTemplateDescription(template = {}) {
      if (template.description) {
        return template.description;
      }
      const base = template.esltemplate_default
        ? "Default layout"
        : "Custom layout";
      const size = template.esltype_code ? ` for ${template.esltype_code}` : "";
      return `${base}${size}`.trim();
    },
    getSupportedSizes(template = {}) {
      const code = template.esltype_code || template.aspect;
      if (!code) {
        return [];
      }
      if (Array.isArray(code)) {
        return code;
      }
      if (typeof code === "string" && code.includes(",")) {
        return code
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);
      }
      return [code];
    },
    getTemplatePreviewAlt(template = {}) {
      return `${this.getTemplateName(template)} preview`;
    },
    async getTemplateImageUrl(template = {}) {
      // return`${process.env.VUE_APP_API_URL_BASE}/fission/e-tag/upload/${fileName}.png`
      const url = `${process.env.VUE_APP_API_URL_BASE}/fission/e-tag/${template.image}`;
      console.log("template.image", url);
      return downloadFile(url);
    },
    async handleApplyToDisplays(template) {
      await this.$loaderWindow({
        title: `Share Settings for ${template.name}`,
        component: () => import("./ApplyTemplateToDisplays.vue"),
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        width: "400px",
        props: {
          templateId: template.id,
        },
      });
      await this.$message.success(`Template applied successfully.`);
    },
    onOpenAddWebSite() {
      window.open(`${process.env.VUE_APP_E_TAG_SERVER_URL_BASE}`, "_blank");
    },
    handleApply() {
      this.filterPanelVisible = false;
      this.reloadTemplates();
    },
  },
};
</script>

<style lang="scss" scoped>
.templates-library {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  flex: 1;
}

.template-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #4b5563;
    font-size: 14px;
  }

  footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: #9ca3af;
  }
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  color: #6b7280;
  padding: 40px 0;
}
</style>
